import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Navigation from '../../services/Navigation';
import LiveStreamSession from '../../services/LiveStreamSession';
import convertUrlfromS3toCloudFront from '../../utils/convertUrlfromS3toCloudFront';
import escapeCssUrl from '../../utils/escapeCssUrl';
import {
  isLiveStreamBroadcasting,
  isLiveStreamContent,
} from '../../utils/liveStreamAccess';
import Constants from '../../submodules/logictry_config/constants';
import {
  RAIL_CARD_WIDTH,
  RAIL_CARD_WIDTH_MOBILE,
  RAIL_CARD_WIDTH_TABLET,
  SEARCH_CONTENT_CARD_WIDTH,
  SEARCH_CONTENT_CARD_WIDTH_MOBILE,
  SEARCH_CONTENT_CARD_WIDTH_TABLET,
  fallbackGradient,
  gradientFromColor,
} from '../../styledhtml/LogicBaseCard';

const Thumb = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  background-color: #000;
  ${(p) => p.$landscape && `
    aspect-ratio: 16 / 9;
    background-color: #e8e8e8;
  `}
`;

const Photo = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  position: relative;
  overflow: hidden;
  background-color: #000;
  ${(p) => p.$fill && `
    position: absolute;
    inset: 0;
    aspect-ratio: auto;
  `}
`;

const HERO_CARD_WIDTH_MOBILE = '19.5rem';
const HERO_CARD_WIDTH_TABLET = '26rem';
const HERO_CARD_WIDTH_DESKTOP = '30rem';

const Card = styled.article`
  width: ${(p) => (p.$hero ? HERO_CARD_WIDTH_MOBILE : p.$compact ? SEARCH_CONTENT_CARD_WIDTH_MOBILE : RAIL_CARD_WIDTH_MOBILE)};
  flex-shrink: 0;
  cursor: pointer;
  box-sizing: border-box;
  background: transparent;
  border: none;
  border-radius: 12px;
  overflow: visible;
  display: block;
  min-width: 0;
  text-align: left;
  position: relative;
  ${Constants.MediaTablet} {
    width: ${(p) => (p.$hero ? HERO_CARD_WIDTH_TABLET : p.$compact ? SEARCH_CONTENT_CARD_WIDTH_TABLET : RAIL_CARD_WIDTH_TABLET)};
  }
  ${Constants.MediaDesktop} {
    width: ${(p) => (p.$hero ? HERO_CARD_WIDTH_DESKTOP : p.$compact ? SEARCH_CONTENT_CARD_WIDTH : RAIL_CARD_WIDTH)};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 3px ${Constants.PrimaryColor};
  }
`;

const Bg = styled.div`
  position: absolute;
  inset: 0;
  background-size: ${(p) => (p.$contain ? 'contain' : 'cover')};
  background-position: top center;
  background-repeat: no-repeat;
  background-color: ${(p) => (p.$contain ? '#000' : '#e8e8e8')};
`;

const FallbackTitle = styled.span`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: ${(p) => (p.$small ? '0.4rem' : '0.85rem')};
  box-sizing: border-box;
  color: #fff;
  pointer-events: none;
`;

const FallbackTitleText = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  height: fit-content;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  letter-spacing: -0.01em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  ${Constants.MediaTabletAndDesktop} {
    -webkit-line-clamp: 4;
  }
`;

const Chip = styled.span`
  position: absolute;
  bottom: 0.45rem;
  right: 0.45rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  background: ${(p) => (p.$live ? '#ea4335' : 'rgba(0, 0, 0, 0.75)')};
  line-height: 1.2;
  pointer-events: none;
  z-index: 2;
`;

/** Eased (smoothstep) stops so the photo melts into the caption's black with no visible band edge. */
const Fade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 34%;
  pointer-events: none;
  background: linear-gradient(
    to top,
    #000 0%,
    rgba(0, 0, 0, 0.987) 8.1%,
    rgba(0, 0, 0, 0.951) 15.5%,
    rgba(0, 0, 0, 0.896) 22.5%,
    rgba(0, 0, 0, 0.825) 29%,
    rgba(0, 0, 0, 0.741) 35.3%,
    rgba(0, 0, 0, 0.648) 41.2%,
    rgba(0, 0, 0, 0.55) 47.1%,
    rgba(0, 0, 0, 0.45) 52.9%,
    rgba(0, 0, 0, 0.352) 58.8%,
    rgba(0, 0, 0, 0.259) 64.7%,
    rgba(0, 0, 0, 0.175) 71%,
    rgba(0, 0, 0, 0.104) 77.5%,
    rgba(0, 0, 0, 0.049) 84.5%,
    rgba(0, 0, 0, 0.013) 91.9%,
    rgba(0, 0, 0, 0) 100%
  );
`;

/**
 * Pulled up over the near-solid bottom of the fade (-4.5% of card width ≈ 8% of
 * photo height, where the gradient is ≥0.98 black) so text sits tight against the
 * visible image and the card is shorter. Positioned so it paints above Bg/Fade.
 */
const Caption = styled.div`
  position: relative;
  margin-top: -4.5%;
  background: transparent;
  padding: 0.4rem 0.65rem 0.45rem;
  color: #fff;
  pointer-events: none;
  ${Constants.MediaTabletAndDesktop} {
    padding: 0.45rem 0.7rem 0.5rem;
  }
`;

const Kicker = styled.div`
  height: calc(0.5625rem * 1.2);
  margin: 0 0 0.28rem;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${Constants.PrimaryColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CaptionTitle = styled.h3`
  margin: 0;
  max-width: 100%;
  height: calc(0.8125rem * 1.15);
  font-size: 0.8125rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${Constants.MediaTabletAndDesktop} {
    height: calc(0.875rem * 1.15);
    font-size: 0.875rem;
  }
`;

const CaptionMeta = styled.div`
  margin: 0.28rem 0 0;
  max-width: 100%;
  height: calc(0.6875rem * 1.25);
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.62);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LiveBadge = styled.span`
  position: absolute;
  top: 0.55rem;
  left: 0.55rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  background: #ea4335;
  line-height: 1.2;
  z-index: 2;
`;

function treeDescriptionText(tree) {
  const raw = tree?.description?.text ?? tree?.description ?? '';
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/\\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseTreeDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatCardDate(d) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function treeMetaText(tree) {
  const description = treeDescriptionText(tree);
  if (description) return description;
  const created = parseTreeDate(tree?.createdTime ?? tree?.ct);
  if (created) return `Created ${formatCardDate(created)}`;
  const updated = parseTreeDate(tree?.updateTime ?? tree?.ut);
  if (updated) return `Updated ${formatCardDate(updated)}`;
  return '';
}

function openPage(tree, collectionId) {
  const id = tree?._id;
  if (!id) return;
  const q = collectionId ? `?collection=${collectionId}` : '';
  Navigation.push(`${Navigation.content}/${id}${q}`);
}

function railChip(stream, isLiveStream) {
  if (!isLiveStream) return null;
  if (isLiveStreamBroadcasting(stream)) return { label: 'Live', live: true };
  return null;
}

ContentRailCard.propTypes = {
  tree: PropTypes.object.isRequired,
  collectionId: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  fullWidth: PropTypes.bool,
  captionTitle: PropTypes.bool,
  compact: PropTypes.bool,
  /** Home livestream rail — same card, larger poster. */
  hero: PropTypes.bool,
  /** Related-rail 178×100 / 160×90 thumbs — 16:9 poster only, tighter inset. */
  small: PropTypes.bool,
  /** Green category line — usually the collection title. */
  kicker: PropTypes.string,
  current: PropTypes.bool,
};

export default function ContentRailCard({
  tree,
  collectionId,
  onClick,
  className,
  style,
  fullWidth,
  captionTitle,
  compact,
  hero,
  small,
  kicker,
  current,
}) {
  const contentId = tree?._id;
  const trackLiveStatus = isLiveStreamContent(tree);
  const [, bump] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    if (!trackLiveStatus || !contentId) return undefined;
    const onUpdate = () => bump();
    LiveStreamSession.onStateUpdate(onUpdate);
    LiveStreamSession.load(contentId);
    return () => {
      LiveStreamSession.offStateUpdate(onUpdate);
    };
  }, [trackLiveStatus, contentId]);

  const title = tree?.title?.text || '';
  const kickerText = (kicker || '').trim();
  const metaText = treeMetaText(tree);
  const landscapePoster = !!small && !fullWidth;
  const showCaption = !!captionTitle;
  const stacked = showCaption && !landscapePoster;
  const bg =
    tree.background?.image && convertUrlfromS3toCloudFront(
      tree.background.image,
      landscapePoster ? 960 : 1280,
      landscapePoster ? 540 : undefined,
    );
  const hasImage = Boolean(bg);
  const thumbStyle = hasImage
    ? { backgroundImage: `url("${escapeCssUrl(bg)}")` }
    : { background: tree.background?.color ? gradientFromColor(tree.background.color) : fallbackGradient(tree._id || title) };
  const displayTitle = title && (title.charAt(0).toUpperCase() + title.slice(1));
  const go = onClick || (() => openPage(tree, collectionId));
  const stream = LiveStreamSession.get(contentId);
  const chip = railChip(stream, trackLiveStatus);
  const showCenteredTitle = !hasImage && !showCaption && !!displayTitle;
  const cardStyle = fullWidth
    ? { width: '100%', maxWidth: '100%', ...style }
    : style;

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-current={current ? 'true' : undefined}
      onClick={go}
      onKeyDown={(e) => e.key === 'Enter' && go()}
      className={className}
      style={cardStyle}
      $compact={!!compact}
      $hero={!!hero}
    >
      <Thumb $landscape={landscapePoster}>
        <Photo $fill={landscapePoster}>
          <Bg style={thumbStyle} $contain={stacked} />
          {stacked && <Fade />}
          {showCenteredTitle && (
            <FallbackTitle $small={!!small}>
              <FallbackTitleText>{displayTitle || 'Untitled'}</FallbackTitleText>
            </FallbackTitle>
          )}
          {showCaption && chip?.live && <LiveBadge>Live</LiveBadge>}
          {!showCaption && chip && <Chip $live={chip.live} aria-hidden>{chip.label}</Chip>}
        </Photo>
        {stacked && (
          <Caption>
            <Kicker>{kickerText}</Kicker>
            <CaptionTitle title={displayTitle || 'Untitled'}>{displayTitle || 'Untitled'}</CaptionTitle>
            <CaptionMeta>{metaText}</CaptionMeta>
          </Caption>
        )}
      </Thumb>
    </Card>
  );
}
